import { Document, Font, Image, Page, Text, View } from '@react-pdf/renderer';
import React, { useEffect, useState } from 'react';
import LatoRegular from "../../utils/fonts/Lato-Regular.ttf";
import LatoBold from '../../utils/fonts/Lato-Bold.ttf';
import moment from 'moment';
import { formatNumber } from '../../utils/Utils';

Font.register({
 family: 'Lato',
 fonts: [
  { src: LatoRegular },
  { src: LatoBold, fontWeight: 'bold' }
 ]
});

const styles = {
 page: {
  padding: 30,
  fontFamily: "Lato",
  backgroundColor: '#FFFFFF',
  size: 'A4 landscape'
 },
 header: {
  flexDirection: 'row',
  alignItems: "center",
  marginBottom: 20,
  borderBottom: '1px solid #E0E0E0',
  paddingBottom: 15
 },
 logoContainer: {
  marginRight: 20
 },
 logo: {
  width: "auto",
  height: 60
 },
 headerText: {
  flex: 1
 },
 companyName: {
  fontSize: 16,
  fontWeight: "bold",
  color: '#2C3E50'
 },
 reportTitle: {
  fontSize: 14,
  fontWeight: "bold",
  marginTop: 5,
  color: '#3498DB'
 },
 section: {
  border: '1px solid #E0E0E0',
  borderRadius: 4,
  padding: 12,
  marginBottom: 20,
  backgroundColor: '#F9F9F9'
 },
 sectionTitle: {
  fontSize: 12,
  fontWeight: "bold",
  marginBottom: 8,
  color: '#2C3E50',
  borderBottom: '1px solid #E0E0E0',
  paddingBottom: 4
 },
 detailGrid: {
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'space-between'
 },
 detailRow: {
  width: '48%',
  flexDirection: "row",
  marginBottom: 6
 },
 detailLabel: {
  fontWeight: "bold",
  fontSize: 9,
  width: "40%",
  marginRight: 10,
  color: '#7F8C8D'
 },
 detailValue: {
  fontSize: 9,
  width: "60%",
  color: '#2C3E50'
 },
 table: {
  border: '1px solid #E0E0E0',
  borderRadius: 4,
  overflow: "hidden",
  marginBottom: 15
 },
 brandHeader: {
  backgroundColor: '#F2F2F2',
  padding: 8,
  fontWeight: 'bold',
  fontSize: 11,
  color: '#2C3E50',
  borderBottom: '1px solid #E0E0E0',
  borderTop: '1px solid #E0E0E0'
 },
 tableHeader: {
  flexDirection: "row",
  backgroundColor: '#3498DB',
  padding: 6
 },
 headerCell: {
  width: "12.5%",
  textAlign: "center",
  fontWeight: "bold",
  fontSize: 9,
  color: '#FFFFFF'
 },
 tableRow: {
  flexDirection: "row",
  borderBottom: '1px solid #E0E0E0',
  padding: 6,
  backgroundColor: '#FFFFFF'
 },
 alternateRow: {
  backgroundColor: '#F5F5F5'
 },
 cell: {
  width: "12.5%",
  textAlign: "center",
  fontSize: 9,
  color: '#2C3E50'
 },
 brandTotalRow: {
  flexDirection: "row",
  backgroundColor: '#5D6D7E',
  padding: 6,
  borderTop: '1px solid #E0E0E0'
 },
 overallTotalRow: {
  flexDirection: "row",
  backgroundColor: '#2C3E50',
  padding: 6,
  borderTop: '1px solid #E0E0E0'
 },
 totalLabel: {
  width: "37.5%",
  textAlign: "right",
  fontWeight: "bold",
  fontSize: 9,
  color: '#FFFFFF',
  paddingRight: 10
 },
 totalValue: {
  width: "12.5%",
  textAlign: "center",
  fontWeight: "bold",
  fontSize: 9,
  color: '#FFFFFF'
 },
 footer: {
  position: 'absolute',
  bottom: 20,
  left: 0,
  right: 0,
  textAlign: 'center',
  fontSize: 8,
  color: '#95A5A6',
  paddingHorizontal: 30
 },
 formulaNote: {
  fontSize: 9,
  color: '#2C3E50',
  marginBottom: 15,
 },
 footerContent: {
  flexDirection: 'row',
  justifyContent: 'space-between'
 },
 percentageContainer: {
  marginBottom: 20,
  border: '1px solid #E0E0E0',
  borderRadius: 4,
  padding: 12,
  backgroundColor: '#F9F9F9'
 },
 percentageTitle: {
  fontSize: 12,
  fontWeight: "bold",
  marginBottom: 8,
  color: '#2C3E50',
  borderBottom: '1px solid #E0E0E0',
  paddingBottom: 4
 },
 percentageGrid: {
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'space-between'
 },
 percentageRow: {
  width: '48%',
  flexDirection: "row",
  marginBottom: 6,
  alignItems: 'center'
 },
 percentageLabel: {
  fontSize: 9,
  width: "60%",
  color: '#2C3E50'
 },
 percentageBarContainer: {
  width: "40%",
  height: 10,
  backgroundColor: '#ECF0F1',
  borderRadius: 5,
  overflow: 'hidden'
 },
 percentageBar: {
  height: '100%',
  backgroundColor: '#3498DB'
 },
 percentageValue: {
  fontSize: 8,
  marginLeft: 5,
  color: '#7F8C8D'
 },
 pageBreak: {
  marginBottom: 20
 }
};

const UpdateStockTakeReportPDF = ({ stock_take_details, tenant_info }) => {

 const [page_data, setPageData] = useState(stock_take_details);

 useEffect(() => {
  setPageData(stock_take_details);
 }, [stock_take_details]);


 const products = stock_take_details?.products || [];

 // Group products by brand (category)
 const productsByBrand = products.reduce((acc, product) => {
  const brand = product.category || 'Uncategorized';
  if (!acc[brand])
  {
   acc[brand] = [];
  }
  acc[brand].push(product);
  return acc;
 }, {});

 // Calculate Brand distribution
 const categoryDistribution = Object.keys(productsByBrand).reduce((acc, brand) => {
  acc[brand] = productsByBrand[brand].length;
  return acc;
 }, {});

 const totalProducts = products.length;
 const categoryPercentages = Object.keys(categoryDistribution).map(brand => ({
  brand,
  percentage: (categoryDistribution[brand] / totalProducts) * 100
 }));

 // Calculate product type distribution for each category and overall
 const productTypeDistribution = {};
 const overallProductTypeDistribution = {};

 Object.keys(productsByBrand).forEach(brand => {
  productTypeDistribution[brand] = {};

  productsByBrand[brand].forEach(product => {
   const type = product.product_type || 'Uncategorized';
   productTypeDistribution[brand][type] = (productTypeDistribution[brand][type] || 0) + 1;
   overallProductTypeDistribution[type] = (overallProductTypeDistribution[type] || 0) + 1;
  });
 });

 // Convert to percentage format
 const productTypePercentagesByBrand = {};
 Object.keys(productTypeDistribution).forEach(brand => {
  productTypePercentagesByBrand[brand] = Object.keys(productTypeDistribution[brand]).map(type => ({
   type,
   percentage: (productTypeDistribution[brand][type] / productsByBrand[brand].length) * 100
  }));
 });

 // Calculate totals for each brand including value totals
 const brandTotals = Object.keys(productsByBrand).map(brand => {
  return productsByBrand[brand].reduce(
   (totals, item) => {
    totals.theoretical += Number(item.new_theoretical_balance) || 0;
    totals.physical += Number(item.physical_stock) || 0;
    totals.variance += Number(item.new_variance) || 0;
    totals.product_value += Number(item?.product_value) || 0;
    totals.totalPhysicalValue += (Number(item.physical_stock) || 0) * (Number(item?.product_value) || 0);
    return totals;
   },
   {
    brand,
    theoretical: 0,
    physical: 0,
    variance: 0,
    product_value: 0,
    totalPhysicalValue: 0
   }
  );
 });

 // Calculate overall totals including value totals
 const overallTotals = products.reduce(
  (totals, item) => {
   totals.total_theoretical_stock += Number(item.new_theoretical_balance) || 0;
   totals.total_physical_stock += Number(item.physical_stock) || 0;
   totals.total_variance += Number(item.new_variance) || 0;
   totals.total_unit_value += Number(item?.product_value) || 0;
   totals.total_physical_value += (Number(item.physical_stock) || 0) * (Number(item?.product_value) || 0);
   return totals;
  },
  {
   total_theoretical_stock: 0,
   total_physical_stock: 0,
   total_variance: 0,
   total_unit_value: 0,
   total_physical_value: 0
  }
 );

 // Render percentage bars for product types
 const renderProductTypePercentages = (percentages) => (
  <View style={styles.percentageGrid}>
   {percentages.map((item, index) => (
    <View key={index} style={styles.percentageRow}>
     <Text style={styles.percentageLabel}>{item.type}:</Text>
     <View style={{ flexDirection: 'row', alignItems: 'center', width: '40%' }}>
      <View style={styles.percentageBarContainer}>
       <View style={[styles.percentageBar, { width: `${item.percentage}%` }]} />
      </View>
      <Text style={styles.percentageValue}>{formatNumber(item.percentage, 1)}%</Text>
     </View>
    </View>
   ))}
  </View>
 );

 // Render percentage bars for categories
 const renderCategoryPercentages = () => (
  <View style={styles.percentageGrid}>
   {page_data?.final_stock_take_report_data?.category_distribution_percentages?.map((item, index) => (
    <View key={index} style={styles.percentageRow}>
     <Text style={styles.percentageLabel}>{item.brand}:</Text>
     <View style={{ flexDirection: 'row', alignItems: 'center', width: '40%' }}>
      <View style={styles.percentageBarContainer}>
       <View style={[styles.percentageBar, { width: `${item.percentage}%` }]} />
      </View>
      <Text style={styles.percentageValue}>{formatNumber(item.percentage, 1)}%</Text>
     </View>
    </View>
   ))}
  </View>
 );

 return (
  <Document title={stock_take_details?.reference_number ? `Final Stock Take ${stock_take_details?.reference_number}` : "Final Stock Take"}>
   {/* First Page */}
   <Page size="A4" style={styles.page} orientation="landscape" wrap>
    {/* Header */}
    <View style={styles.header}>
     {tenant_info?.tenant_logo && (
      <View style={styles.logoContainer}>
       <Image style={styles.logo} src={tenant_info?.tenant_logo} />
      </View>
     )}
     <View style={styles.headerText}>
      <Text style={styles.companyName}>{tenant_info?.name}</Text>
      <Text style={styles.reportTitle}>Final Stock Take Report</Text>
     </View>
    </View>

    {/* Stock Take Details */}
    <View style={styles.section}>
     <Text style={styles.sectionTitle}>Updated Stock Take Details</Text>
     <View style={styles.detailGrid}>
      {[
       { label: "Name:", value: stock_take_details?.name },
       { label: "Reference Number:", value: stock_take_details?.reference_number },
       { label: "Report Date:", value: stock_take_details?.inserted_at ? moment(stock_take_details?.inserted_at).format("YYYY-MM-DD HH:mm:ss") : "" },
       { label: "Created By:", value: stock_take_details?.employee?.user?.first_name ? `${stock_take_details?.employee?.user?.first_name} ${stock_take_details?.employee?.user?.last_name}` : "--" },
       { label: "Warehouse Name:", value: stock_take_details?.warehouse?.name },
       { label: "Stock Balance Start Date:", value: stock_take_details?.stock_balance_start_date ? moment(stock_take_details?.stock_balance_start_date).format("YYYY-MM-DD HH:mm:ss") : "" },
       { label: "Stock Balance End Date:", value: stock_take_details?.stock_balance_end_date ? moment(stock_take_details?.stock_balance_end_date).format("YYYY-MM-DD HH:mm:ss") : "" },
      ].map((item, index) => (
       <View key={index} style={styles.detailRow}>
        <Text style={styles.detailLabel}>{item.label}</Text>
        <Text style={styles.detailValue}>{item.value || "--"}</Text>
       </View>
      ))}
     </View>
    </View>

    {/* Brand Distribution */}
    <View style={styles.percentageContainer}>
     <Text style={styles.percentageTitle}>Brand Distribution ({page_data?.final_stock_take_report_data?.total_brands} total Brands)</Text>
     {renderCategoryPercentages()}
    </View>

    {/* Overall Product Type Distribution */}
    <View style={styles.percentageContainer}>
     <Text style={styles.percentageTitle}>Overall Product Type Distribution</Text>
     {renderProductTypePercentages(page_data?.final_stock_take_report_data?.product_type_result_distribution_percentages)}
    </View>

    {/* Variance Formula */}
    <Text style={styles.formulaNote}>
     Variance = Theoretical Stock - Physical Stock
    </Text>

    {/* Footer */}
    <View style={styles.footer} fixed>
     <View style={styles.footerContent}>
      <Text>Ref: {stock_take_details?.reference_number || 'N/A'}</Text>
      <Text>Generated on {moment().format('YYYY-MM-DD HH:mm:ss')}</Text>
      <Text>Page <Text render={({ pageNumber, totalPages }) => (
       `${pageNumber} / ${totalPages}`
      )} fixed /></Text>
     </View>
    </View>
   </Page>

   {/* Pages for each category */}
   {
    page_data?.final_stock_take_report_data?.grouped_categories?.map((data_map, brandIndex) => {
     return (
      <>
       <Page key={brandIndex} size="A4" style={styles.page} orientation="landscape" wrap>
        {/* Header */}
        <View style={styles.header}>
         {tenant_info?.tenant_logo && (
          <View style={styles.logoContainer}>
           <Image style={styles.logo} src={tenant_info?.tenant_logo} />
          </View>
         )}
         <View style={styles.headerText}>
          <Text style={styles.companyName}>{tenant_info?.name}</Text>
          <Text style={styles.reportTitle}>Initial Stock Take Report - {data_map?.brand}</Text>
         </View>
        </View>

        {/* Category Product Type Distribution */}
        <View style={styles.percentageContainer}>
         <Text style={styles.percentageTitle}>
          {data_map?.brand} Product Type Distribution ({data_map?.products_length} products, {formatNumber(data_map?.percentage)}% of total)
         </Text>
         {renderProductTypePercentages(data_map?.cat_product_type_result_distribution_percentages)}
        </View>

        {/* Variance Formula */}
        <Text style={styles.formulaNote}>
         Variance = Theoretical Stock - Physical Stock
        </Text>

        {/* Products Table */}
        <View style={styles.table}>
         <View style={styles.tableHeader}>
          {["Name", "Color", "Size", "Theoretical", "Physical", "Variance", "Unit Value (ZMW)", "Total Value (Physical)"].map((header, index) => (
           <Text key={index} style={styles.headerCell}>{header}</Text>
          ))}
         </View>
        </View>
        {/* Brand Products */}
        {data_map?.products?.map((item, index) => (
         <View
          key={index}
          style={[
           styles.tableRow,
           index % 2 === 0 && styles.alternateRow
          ]}
         >
          {[
           item.product_name,
           item.color,
           item.size,
           item.new_theoretical_balance,
           item.physical_stock ? formatNumber(item.physical_stock) : 0.0,
           item.new_variance,
           item.product_value,
           (Number(item?.physical_stock) || 0) * (Number(item?.product_value) || 0)
          ].map((value, i) => (
           <Text key={i} style={styles.cell}>
            {typeof value === 'number' ? formatNumber(value) : value || "--"}
           </Text>
          ))}
         </View>
        ))}

        {/* Brand Total Row */}
        <View style={styles.brandTotalRow}>
         <Text style={styles.totalLabel}>{data_map?.brand} Total:</Text>
         <Text style={styles.totalValue}>
          {formatNumber(data_map?.theoretical_total || 0.0)}
         </Text>
         <Text style={styles.totalValue}>
          {formatNumber(data_map?.physical_total || 0.0)}
         </Text>
         <Text style={styles.totalValue}>
          {formatNumber(data_map?.variance_total || 0.0)}
         </Text>
         <View style={styles.totalValue}></View>
         <Text style={styles.totalValue}>
          {formatNumber(data_map?.physical_variance_total || 0.0)}
         </Text>
        </View>

        {/* Footer */}
        <View style={styles.footer} fixed>
         <View style={styles.footerContent}>
          <Text>Ref: {stock_take_details?.reference_number || 'N/A'}</Text>
          <Text>Generated on {moment().format('YYYY-MM-DD HH:mm:ss')}</Text>
          <Text>Page <Text render={({ pageNumber, totalPages }) => (
           `${pageNumber} / ${totalPages}`
          )} fixed /></Text>
         </View>
        </View>

       </Page>
      </>
     );
    })
   }

   {/* Final Page with Overall Totals */}
   <Page size="A4" style={styles.page} orientation="landscape" wrap>
    {/* Header */}
    <View style={styles.header}>
     {tenant_info?.tenant_logo && (
      <View style={styles.logoContainer}>
       <Image style={styles.logo} src={tenant_info?.tenant_logo} />
      </View>
     )}
     <View style={styles.headerText}>
      <Text style={styles.companyName}>{tenant_info?.name}</Text>
      <Text style={styles.reportTitle}>Initial Stock Take Report - Summary</Text>
     </View>
    </View>

    {/* Overall Totals */}
    <View style={styles.section}>
     <Text style={styles.sectionTitle}>Stock Take Summary</Text>
     <View style={styles.detailGrid}>
      total_variance_value_total: total_variance_value_total,
      total_physical_stock: total_physical_stock,
      total_physical_variance_total: total_varianctotal_physical_variance_totale,
      {[
       { label: "Total Theoretical Stock:", value: formatNumber(page_data?.final_stock_take_report_data?.total_theoretical_stock) },
       { label: "Total Physical Stock:", value: formatNumber(page_data?.final_stock_take_report_data?.total_physical_stock) },
       { label: "Total Variance:", value: formatNumber(page_data?.final_stock_take_report_data?.total_variance) },
       { label: "Total Physical Value (ZMW):", value: formatNumber(page_data?.final_stock_take_report_data?.total_physical_value_total) },
       { label: "Total Products Count:", value: stock_take_details?.products?.length },
       { label: "Total Categories:", value: page_data?.final_stock_take_report_data?.total_brands },
      ].map((item, index) => (
       <View key={index} style={styles.detailRow}>
        <Text style={styles.detailLabel}>{item.label}</Text>
        <Text style={styles.detailValue}>{item.value || "--"}</Text>
       </View>
      ))}
     </View>
    </View>

    {/* Brand Distribution */}
    <View style={styles.percentageContainer}>
     <Text style={styles.percentageTitle}>Brand Distribution ({page_data?.final_stock_take_report_data?.total_brands} total Brands)</Text>
     {renderCategoryPercentages()}
    </View>

    {/* Overall Product Type Distribution */}
    <View style={styles.percentageContainer}>
     <Text style={styles.percentageTitle}>Overall Product Type Distribution</Text>
     {renderProductTypePercentages(page_data?.final_stock_take_report_data?.product_type_result_distribution_percentages)}
    </View>

    {/* Footer */}
    <View style={styles.footer} fixed>
     <View style={styles.footerContent}>
      <Text>Ref: {stock_take_details?.reference_number || 'N/A'}</Text>
      <Text>Generated on {moment().format('YYYY-MM-DD HH:mm:ss')}</Text>
      <Text>Page <Text render={({ pageNumber, totalPages }) => (
       `${pageNumber} / ${totalPages}`
      )} fixed /></Text>
     </View>
    </View>
   </Page>
  </Document>
 );
};

export default UpdateStockTakeReportPDF;